import { useState, useEffect, useRef } from "react";

function useMultiMonitor() {
  const [isMultiMonitor, setIsMultiMonitor] = useState(false);
  const debounceTimeoutRef = useRef(null);
  const lastCheckResultRef = useRef(false);
  const isFullscreenRef = useRef(false);

  useEffect(() => {
    const checkMultiMonitor = () => {
      // Clear any pending debounced calls
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Debounce the actual check to avoid rapid-fire calls during transitions
      debounceTimeoutRef.current = setTimeout(() => {
        performMultiMonitorCheck();
      }, 200);
    };

    const performMultiMonitorCheck = () => {
      try {
        // Track current fullscreen state
        const currentlyFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
        );
        
        isFullscreenRef.current = currentlyFullscreen;

        // Method 1: Modern API (Chromium browsers)
        const isExtended = "isExtended" in window.screen ? window.screen.isExtended : false;
        
        // Method 2: Screen width comparison (more reliable)
        // Only check this when not in fullscreen to avoid false positives
        const totalWidth = window.screen.width;
        const availableWidth = window.screen.availWidth;
        const widthHint = !currentlyFullscreen && availableWidth > totalWidth;
        
        // Method 3: Window position hint
        // Check if window is positioned on a secondary monitor
        const positionHint = !currentlyFullscreen && (
          window.screenLeft < 0 || 
          window.screenLeft > window.screen.width ||
          window.screenTop < 0 ||
          window.screenTop > window.screen.height
        );
        
        // Method 4: Media query (if supported)
        let matchMediaHint = false;
        try {
          if (window.matchMedia) {
            matchMediaHint = window.matchMedia("(display-mode: extended)").matches;
          }
        } catch (e) {
          // Ignore media query errors
        }

        const hasMultipleMonitors = !!(
          isExtended || 
          widthHint || 
          positionHint || 
          matchMediaHint
        );

        updateMultiMonitorState(hasMultipleMonitors);
      } catch (error) {
        console.warn("Error in multi-monitor detection:", error);
        // Don't change state on error
      }
    };

    const updateMultiMonitorState = (hasMultiple) => {
      // Only update state if the result has actually changed
      if (lastCheckResultRef.current !== hasMultiple) {
        lastCheckResultRef.current = hasMultiple;
        setIsMultiMonitor(hasMultiple);
        
        if (hasMultiple) {
          console.log("Multi-monitor setup detected");
        } else {
          console.log("Single monitor setup detected");
        }
      }
    };

    const handleFullscreenChange = () => {
      // Wait a bit after fullscreen changes to let screen properties stabilize
      setTimeout(() => {
        checkMultiMonitor();
      }, 500);
    };

    const handleVisibilityChange = () => {
      // Check when tab becomes visible again (might indicate monitor changes)
      if (!document.hidden) {
        setTimeout(checkMultiMonitor, 300);
      }
    };

    // Initial check
    checkMultiMonitor();

    // Event listeners for various monitor/screen changes
    window.addEventListener("resize", checkMultiMonitor);
    window.addEventListener("orientationchange", checkMultiMonitor);
    
    // Fullscreen change listeners
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);
    
    // Visibility change (tab switching can indicate monitor usage)
    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    // Screen change listener (if supported)
    if ("onchange" in window.screen) {
      window.screen.addEventListener("change", checkMultiMonitor);
    }

    // Focus events (window focus changes might indicate monitor switches)
    window.addEventListener("focus", checkMultiMonitor);
    window.addEventListener("blur", checkMultiMonitor);

    // Storage event listener (detect if user is using multiple windows)
    const handleStorageChange = () => {
      // This is a subtle way to detect multiple windows/monitors
      setTimeout(checkMultiMonitor, 100);
    };
    window.addEventListener("storage", handleStorageChange);

    // Periodic check every 10 seconds as fallback
    const intervalId = setInterval(() => {
      // Only do periodic checks if not in fullscreen
      if (!isFullscreenRef.current) {
        checkMultiMonitor();
      }
    }, 10000);

    return () => {
      // Cleanup
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      clearInterval(intervalId);
      
      window.removeEventListener("resize", checkMultiMonitor);
      window.removeEventListener("orientationchange", checkMultiMonitor);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", checkMultiMonitor);
      window.removeEventListener("blur", checkMultiMonitor);
      window.removeEventListener("storage", handleStorageChange);
      
      if ("onchange" in window.screen) {
        window.screen.removeEventListener("change", checkMultiMonitor);
      }
    };
  }, []);

  return isMultiMonitor;
}

export default useMultiMonitor;